import { storableError } from '../../util/errors';
import { getSeoLanding } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { placeholderSeoCity, seoLandingFor } from '../../util/seoLandings';

// La página llega ya calculada por el job diario (server/seoLandings/): el municipio, ids de las
// tarjetas, las tarjetas en sí, reseñas, precios y municipios cercanos. Una visita no consulta
// Sharetribe.
// Ver docs/seo-landings.md.

// ================ Action types ================ //

export const FETCH_SEO_LANDING_REQUEST = 'app/SeoLandingPage/FETCH_SEO_LANDING_REQUEST';
export const FETCH_SEO_LANDING_SUCCESS = 'app/SeoLandingPage/FETCH_SEO_LANDING_SUCCESS';
export const FETCH_SEO_LANDING_ERROR = 'app/SeoLandingPage/FETCH_SEO_LANDING_ERROR';

// ================ Reducer ================ //

const initialState = {
  pageKey: null,
  // El municipio: `{ slug, name, location }`.
  city: null,
  listingIds: [],
  // Las páginas que existen en el municipio (slugs), para "Más ideas".
  availablePages: [],
  // Municipios cercanos con la misma página: `[{ slug, name, km }]`.
  nearby: [],
  // Reseñas del bloque: `[{ listingId, review }]` (review con su autor ya resuelto).
  reviews: [],
  // Precio mínimo y mediana por hora de la página (subunidades): `{ min, median, count }`.
  priceStats: null,
  // La página no existe (menos de 2 anuncios): 404.
  notFound: false,
  fetchInProgress: false,
  fetchError: null,
};

/** Clave del estado: municipio y página. */
export const seoLandingPageKey = ({ city, category } = {}) => `${city}/${category}`;

export default function seoLandingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SEO_LANDING_REQUEST:
      return { ...initialState, pageKey: payload.pageKey, fetchInProgress: true };
    case FETCH_SEO_LANDING_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        city: payload.city,
        listingIds: payload.listingIds,
        availablePages: payload.availablePages,
        nearby: payload.nearby,
        reviews: payload.reviews,
        priceStats: payload.priceStats,
        notFound: payload.notFound,
      };
    case FETCH_SEO_LANDING_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Thunks ================ //

const NOT_FOUND = {
  city: null,
  listingIds: [],
  availablePages: [],
  nearby: [],
  reviews: [],
  priceStats: null,
  notFound: true,
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  if (!seoLandingFor(placeholderSeoCity(params.city), params.category)) {
    return Promise.resolve();
  }
  const pageKey = seoLandingPageKey(params);
  // Al hidratar, el cliente vuelve a llamar a loadData: si el servidor ya trajo esta página, no
  // se pide otra vez (ni se vacía el estado mientras llega, que hacía parpadear las tarjetas).
  const current = getState && getState().SeoLandingPage;
  if (current && current.pageKey === pageKey && !current.fetchInProgress && !current.fetchError) {
    return Promise.resolve();
  }
  dispatch({ type: FETCH_SEO_LANDING_REQUEST, payload: { pageKey } });

  return getSeoLanding(`/${pageKey}`, config?.ssrApiBaseUrl)
    .then(page => {
      if (!page) {
        return NOT_FOUND;
      }
      const sanitizeConfig = { listingFields: config?.listing?.listingFields };
      dispatch(addMarketplaceEntities({ data: page.cards }, sanitizeConfig));
      const cardIds = new Map(page.cards.data.map(l => [l.id.uuid, l.id]));
      return {
        city: page.city,
        listingIds: page.listingIds.map(id => cardIds.get(id)).filter(Boolean),
        availablePages: page.availablePages || [],
        nearby: page.nearby || [],
        reviews: page.reviews || [],
        priceStats: page.priceStats || null,
        notFound: false,
      };
    })
    .then(payload => dispatch({ type: FETCH_SEO_LANDING_SUCCESS, payload }))
    .catch(e => {
      dispatch({ type: FETCH_SEO_LANDING_ERROR, payload: storableError(e), error: true });
    });
};
